import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface PortalMenuProps {
  /** the element the menu hangs off — usually the dropdown trigger wrapper */
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  /** the widget's own `.menu` class (padding, border, shadow, colours) */
  className?: string;
  children: React.ReactNode;
}

const GAP = 6;

/**
 * Dropdown panel rendered into the dashboard root instead of into the widget
 * card that owns the trigger.
 *
 * Widget cards clip their content (`overflow: hidden` keeps the rounded corners
 * honest), so a menu positioned inside a card gets cut off as soon as it is
 * taller than the space left below the trigger — which is what happened once
 * the growth widget grew to seven metrics. Re-parenting to the dashboard root
 * removes that whole class of bug: no card can clip it.
 *
 * The root is also the theme scope (`[data-ho-theme]`), so the menu keeps every
 * `--ho-*` colour variable it styles with — parenting to `document.body` would
 * strip the palette. And because the menu is positioned *within* that root
 * rather than against the viewport, it scrolls with the page on its own: no
 * per-scroll JS repositioning, so it can neither lag behind the trigger nor
 * drift behind the sticky header on the way up.
 */
export default function PortalMenu({
  anchorRef,
  open,
  onClose,
  className,
  children,
}: PortalMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [style, setStyle] = useState<React.CSSProperties | null>(null);

  useEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }

    const el = anchorRef.current;
    if (!el) return;
    const root = el.closest("[data-ho-theme]") as HTMLElement | null;
    if (!root) return;
    // The offsets below are measured against the root, so the root has to be
    // the menu's containing block. `.scope` sets `position: relative` for that;
    // this is the guarantee — a static root would silently push the menu off to
    // wherever the document's initial containing block happens to be.
    if (getComputedStyle(root).position === "static") {
      root.style.position = "relative";
    }
    setHost(root);

    // Offsets are relative to the dashboard root (its own containing block),
    // NOT the viewport — that's what lets the menu ride along with the scroll.
    const place = () => {
      const a = el.getBoundingClientRect();
      const h = root.getBoundingClientRect();
      setStyle({
        position: "absolute",
        top: a.bottom - h.top + GAP,
        right: Math.max(0, h.right - a.right),
        // tall lists scroll inside the menu rather than running off the page
        maxHeight: "min(60vh, 420px)",
        overflowY: "auto",
        // under the dashboard's sticky header (z-index 200), over everything
        // inside the widget grid
        zIndex: 150,
      });
    };

    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !host || !style) return null;

  return createPortal(
    <div ref={menuRef} className={className} style={style}>
      {children}
    </div>,
    host,
  );
}
